import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { toast } from "sonner";
import {
  Phone, X, Pin, FileText, Mic, Square, Play, Pause, UserPlus,
  StickyNote, TriangleAlert, Camera, ListTodo, MapPin, CheckCircle2,
} from "lucide-react";

export function fileUrl(path) {
  const token = localStorage.getItem("fl_token");
  return `${API}/files/view?path=${encodeURIComponent(path)}${token ? `&auth=${token}` : ""}`;
}

function MemberAvatar({ m, size = 30 }) {
  const initials = (m.name || "?").split(" ").map((s) => s.replace(/[^A-Za-z0-9]/g, "")[0]).filter(Boolean).join("").slice(0, 2).toUpperCase();
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <div className="w-full h-full rounded-full bg-[#09090B] text-white flex items-center justify-center overflow-hidden font-display font-black ring-2 ring-[#0B1018]"
           style={{ fontSize: Math.max(9, size * 0.34) }}>
        {m.avatar_path ? <AuthImage path={m.avatar_path} alt={m.name} className="w-full h-full object-cover" /> : initials}
      </div>
      {m.online && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-[#0B1018]" />}
    </div>
  );
}

/* ------------------------- members strip + call sheet ------------------------- */

export function MembersStrip({ members, onCall, canInvite }) {
  if (!members) return null;
  const online = members.members?.filter((m) => m.online) || [];
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 bg-[#0B1018] shrink-0" data-testid="chat-members-strip">
      <button type="button" onClick={onCall} className="flex -space-x-2" data-testid="chat-members-avatars">
        {(members.members || []).slice(0, 5).map((m) => <MemberAvatar key={m.id} m={m} size={28} />)}
      </button>
      <button type="button" onClick={onCall} className="text-xs text-zinc-300 text-left leading-tight">
        <span className="font-bold">{members.total} members</span>
        <span className="inline-flex items-center gap-1 ml-1.5 text-emerald-600 dark:text-emerald-400">
          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> {members.online} online
        </span>
      </button>
      {canInvite && (
        <Link to="/app/team" className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border border-[#FF5F15] rounded-full px-3 py-1.5 text-[#FF5F15] hover:bg-[#FF5F15]/10 transition-colors" data-testid="chat-invite-btn">
          <UserPlus className="w-3 h-3" /> Invite
        </Link>
      )}
    </div>
  );
}

export function CallSheet({ open, onClose, members }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="chat-call-sheet">
      <div className="bg-[#161618] w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <span className="font-display font-black uppercase tracking-tight text-sm text-white">Call a crew member</span>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 hover:text-white" data-testid="call-sheet-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto divide-y divide-zinc-800/60">
          {(members?.members || []).map((m) => (
            <div key={m.id} className="flex items-center gap-3 px-4 py-2.5">
              <MemberAvatar m={m} size={34} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-zinc-100">{m.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{m.role}{m.online ? " · online" : ""}</div>
              </div>
              {m.phone ? (
                <a href={`tel:${m.phone}`} className="w-9 h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center" data-testid={`call-member-${m.id}`} aria-label={`Call ${m.name}`}>
                  <Phone className="w-4 h-4" />
                </a>
              ) : (
                <span className="text-[10px] text-zinc-400">no number</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- pinned strip --------------------------------- */

export function PinnedStrip({ pins, onOpenAll, onJump }) {
  if (!pins || pins.length === 0) return null;
  const p = pins[0];
  return (
    <div className="px-3 pt-2 pb-1 bg-[#0B1018] shrink-0">
      <div className="rounded-2xl bg-[#101620] border border-zinc-800 p-3" data-testid="chat-pinned-strip">
        <div className="flex items-center justify-between mb-2">
          <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-zinc-100">
            <Pin className="w-3.5 h-3.5 text-[#FF5F15]" /> Pinned
          </span>
          <button type="button" onClick={onOpenAll} className="text-[11px] font-bold text-[#FF5F15]" data-testid="pinned-view-all">
            View all{pins.length > 1 ? ` (${pins.length})` : ""}
          </button>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-[#FF5F15]/15 border border-[#FF5F15]/30 flex items-center justify-center shrink-0">
            <FileText className="w-4 h-4 text-[#FF5F15]" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-zinc-100 truncate">
              {p.text ? p.text.slice(0, 60) : p.audio_path ? "Voice note" : "Photo"}
            </div>
            <div className="text-[10px] text-zinc-500 truncate">
              Pinned by {p.pinned?.by_name} · {new Date(p.pinned?.at || p.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- audio bubble --------------------------------- */

export function AudioBubble({ path, seconds, mine }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); } else { a.play(); }
  };

  return (
    <div className="flex items-center gap-2.5 min-w-[180px]" data-testid="chat-audio-bubble">
      <audio
        ref={audioRef}
        src={fileUrl(path)}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onTimeUpdate={(e) => {
          const a = e.target;
          if (a.duration) setProgress(a.currentTime / a.duration);
        }}
      />
      <button type="button" onClick={toggle}
              className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${mine ? "bg-white/25 text-white" : "bg-[#FF5F15] text-white"}`}
              data-testid="audio-play-btn" aria-label={playing ? "Pause voice note" : "Play voice note"}>
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
      <div className="flex-1">
        <div className={`h-1.5 rounded-full ${mine ? "bg-white/30" : "bg-zinc-300 dark:bg-zinc-700"}`}>
          <div className={`h-full rounded-full ${mine ? "bg-white" : "bg-[#FF5F15]"}`} style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
        <div className={`text-[10px] font-mono mt-1 ${mine ? "text-white/80" : "text-zinc-500"}`}>
          <Mic className="w-3 h-3 inline mr-1" />{seconds ? `${Math.round(seconds)}s` : "voice note"}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- voice recorder --------------------------------- */

export function VoiceButton({ onSend, disabled }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const cancelledRef = useRef(false);

  const stopTimer = () => { clearInterval(timerRef.current); };

  const start = async () => {
    if (disabled || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      cancelledRef.current = false;
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        stopTimer();
        setRecording(false);
        if (cancelledRef.current) return;
        const secs = (Date.now() - startedAtRef.current) / 1000;
        if (secs < 1) { toast.message("Hold to record a voice note"); return; }
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        const ext = (rec.mimeType || "").includes("mp4") ? "m4a" : "webm";
        const fd = new FormData();
        fd.append("file", new File([blob], `voice-note.${ext}`, { type: blob.type }));
        fd.append("category", "chat_audio");
        try {
          const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          onSend(data.storage_path || data.path, Math.round(secs));
        } catch (err) {
          toast.error(err.response?.data?.detail || "Voice note upload failed");
        }
      };
      recRef.current = rec;
      rec.start();
      startedAtRef.current = Date.now();
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000)), 500);
      setRecording(true);
    } catch {
      toast.error("Microphone not available — check permissions");
    }
  };

  const stop = (cancel = false) => {
    cancelledRef.current = cancel;
    try { recRef.current?.stop(); } catch { /* noop */ }
  };

  useEffect(() => () => { stopTimer(); try { recRef.current?.stream?.getTracks()?.forEach((t) => t.stop()); } catch {} }, []);

  if (recording) {
    return (
      <div className="flex items-center gap-2" data-testid="chat-recording-bar">
        <span className="text-xs font-mono text-red-600 animate-pulse whitespace-nowrap">● {elapsed}s</span>
        <button type="button" onClick={() => stop(true)} className="text-[10px] font-bold uppercase text-zinc-500 hover:text-red-600 px-1" data-testid="voice-cancel-btn">Cancel</button>
        <button type="button" onClick={() => stop(false)}
                className="w-11 h-11 rounded-full bg-red-600 text-white flex items-center justify-center shrink-0"
                data-testid="voice-stop-btn" aria-label="Stop and send voice note">
          <Square className="w-4 h-4" />
        </button>
      </div>
    );
  }
  return (
    <button type="button" onClick={start} disabled={disabled}
            className="w-12 h-12 rounded-full bg-[#FF5F15] hover:bg-[#E65100] text-white flex items-center justify-center shrink-0 disabled:opacity-40"
            data-testid="chat-voice-button" aria-label="Record voice note">
      <Mic className="w-[22px] h-[22px]" />
    </button>
  );
}

/* --------------------------------- quick actions --------------------------------- */

export function QuickActionsBar({ onSiteNote, onHazard, onPhoto, onTask }) {
  const Item = ({ icon: Icon, label, onClick, testid }) => (
    <button type="button" onClick={onClick}
            className="flex flex-col items-center gap-2 py-3 rounded-2xl bg-[#151C27] text-zinc-100 hover:bg-[#26272C] active:scale-95 transition-all"
            data-testid={testid}>
      <Icon className="w-[22px] h-[22px] text-[#FF5F15]" strokeWidth={1.9} />
      <span className="text-[12px] font-semibold">{label}</span>
    </button>
  );
  return (
    <div className="grid grid-cols-4 gap-2.5 px-3 pt-1.5 pb-[max(0.85rem,env(safe-area-inset-bottom))] bg-[#0B1018] shrink-0" data-testid="chat-quick-actions">
      <Item icon={StickyNote} label="Site Note" onClick={onSiteNote} testid="qa-site-note" />
      <Item icon={TriangleAlert} label="Hazard" onClick={onHazard} testid="qa-hazard" />
      <Item icon={Camera} label="Photo" onClick={onPhoto} testid="qa-photo" />
      <Item icon={ListTodo} label="Task" onClick={onTask} testid="qa-task" />
    </div>
  );
}

/* --------------------------------- job pick dialogs --------------------------------- */

function useJobs() {
  const [jobs, setJobs] = useState([]);
  useEffect(() => {
    api.get("/jobs").then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data.items || [];
      setJobs(list.filter((j) => !["completed", "cancelled"].includes(j.status)));
    }).catch(() => {});
  }, []);
  return jobs;
}

export function SiteNoteDialog({ open, onClose, message, onDone }) {
  const jobs = useJobs();
  const [jobId, setJobId] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open || !message) return null;

  const save = async () => {
    if (!jobId) { toast.error("Pick a job first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/chat/messages/${message.id}/site-note`, { job_id: jobId });
      toast.success("Saved to the job's site diary");
      onDone(message.id, data.site_note);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the note");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="site-note-dialog">
      <div className="bg-[#161618] w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl p-4" onClick={(e) => e.stopPropagation()}>
        <div className="font-display font-black uppercase tracking-tight text-sm text-white flex items-center gap-2">
          <StickyNote className="w-4 h-4 text-[#FF5F15]" /> Save as site note
        </div>
        <div className="mt-2 text-xs bg-zinc-100 dark:bg-zinc-800 text-zinc-200 rounded-lg p-2.5 max-h-24 overflow-y-auto">"{message.text}"</div>
        <select value={jobId} onChange={(e) => setJobId(e.target.value)}
                className="mt-3 w-full border border-zinc-700 rounded-lg px-3 py-2.5 text-sm bg-[#1A1B1E] text-zinc-100"
                data-testid="site-note-job-select">
          <option value="">Which job is this about?</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.job_number ? `${j.job_number} — ` : ""}{j.title}</option>)}
        </select>
        <div className="mt-3 flex gap-2">
          <button onClick={onClose} className="flex-1 h-10 rounded-lg border border-zinc-700 text-sm font-bold uppercase tracking-wider text-zinc-200">Cancel</button>
          <button onClick={save} disabled={busy} className="flex-1 h-10 rounded-lg bg-[#FF5F15] text-white text-sm font-bold uppercase tracking-wider disabled:opacity-50" data-testid="site-note-save-btn">
            {busy ? "Saving…" : "Save note"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function TaskDialog({ open, onClose, onCreated }) {
  const jobs = useJobs();
  const [jobId, setJobId] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const save = async () => {
    if (!jobId || !title.trim()) { toast.error("Pick a job and give the task a title"); return; }
    setBusy(true);
    try {
      await api.post("/chat/tasks", { job_id: jobId, title: title.trim() });
      toast.success("Task created");
      setTitle(""); setJobId("");
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create the task");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="chat-task-dialog">
      <div className="bg-[#161618] w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl p-4" onClick={(e) => e.stopPropagation()}>
        <div className="font-display font-black uppercase tracking-tight text-sm text-white flex items-center gap-2">
          <ListTodo className="w-4 h-4 text-[#FF5F15]" /> New task
        </div>
        <select value={jobId} onChange={(e) => setJobId(e.target.value)}
                className="mt-3 w-full border border-zinc-700 rounded-lg px-3 py-2.5 text-sm bg-[#1A1B1E] text-zinc-100"
                data-testid="chat-task-job-select">
          <option value="">Which job?</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.job_number ? `${j.job_number} — ` : ""}{j.title}</option>)}
        </select>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs doing?"
               className="mt-2 w-full border border-zinc-700 rounded-lg px-3 py-2.5 text-sm bg-[#1A1B1E] text-zinc-100"
               data-testid="chat-task-title-input" />
        <div className="mt-3 flex gap-2">
          <button onClick={onClose} className="flex-1 h-10 rounded-lg border border-zinc-700 text-sm font-bold uppercase tracking-wider text-zinc-200">Cancel</button>
          <button onClick={save} disabled={busy} className="flex-1 h-10 rounded-lg bg-[#FF5F15] text-white text-sm font-bold uppercase tracking-wider disabled:opacity-50" data-testid="chat-task-save-btn">
            {busy ? "Creating…" : "Create task"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- tab panels --------------------------------- */

export function FilesTab({ channelId, onPhotoClick }) {
  const [files, setFiles] = useState(null);
  useEffect(() => {
    api.get(`/chat/channels/${channelId}/files`).then((r) => setFiles(r.data)).catch(() => setFiles([]));
  }, [channelId]);
  if (!files) return <div className="p-6 text-sm text-zinc-500">Loading files…</div>;
  if (files.length === 0) return <div className="p-8 text-center text-sm text-zinc-500">No photos or voice notes shared yet.</div>;
  const photos = files.filter((f) => f.kind === "photo");
  const audio = files.filter((f) => f.kind === "audio");
  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-4" data-testid="chat-files-tab">
      {photos.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Photos ({photos.length})</div>
          <div className="grid grid-cols-3 gap-1.5">
            {photos.map((f, i) => (
              <button key={i} onClick={() => onPhotoClick(f.path)} className="aspect-square rounded-lg overflow-hidden bg-zinc-800" data-testid={`files-photo-${i}`}>
                <AuthImage path={f.path} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
      {audio.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Voice notes ({audio.length})</div>
          <div className="space-y-2">
            {audio.map((f, i) => (
              <div key={i} className="bg-[#101620] border border-zinc-800 rounded-xl p-3">
                <AudioBubble path={f.path} seconds={f.seconds} mine={false} />
                <div className="text-[10px] text-zinc-500 mt-1.5">{f.by} · {new Date(f.at).toLocaleDateString("en-NZ")}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function TasksTab({ refreshKey, onNewTask }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/chat/tasks").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, [refreshKey]);
  const done = async (t) => {
    try {
      await api.post(`/job-tasks/${t.id}/toggle`);
      setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== t.id) }));
      toast.success("Task ticked off");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't complete task"); }
  };
  return (
    <div className="flex-1 overflow-y-auto p-3" data-testid="chat-tasks-tab">
      <button onClick={onNewTask} className="w-full h-10 rounded-lg bg-[#FF5F15] text-white text-xs font-bold uppercase tracking-wider mb-3" data-testid="tasks-tab-new-btn">+ New task</button>
      {!data ? <div className="text-sm text-zinc-500">Loading…</div> :
        data.items.length === 0 ? <div className="p-6 text-center text-sm text-zinc-500">No open tasks. Sweet as.</div> :
        <div className="space-y-2">
          {data.items.map((t) => (
            <div key={t.id} className="bg-[#101620] border border-zinc-800 rounded-xl p-3 flex items-center gap-3" data-testid={`chat-task-row-${t.id}`}>
              <button onClick={() => done(t)} className="text-zinc-300 hover:text-emerald-600" data-testid={`chat-task-done-${t.id}`} aria-label="Mark done">
                <CheckCircle2 className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-100 truncate">{t.title}</div>
                <div className="text-[10px] text-zinc-500 truncate">{t.job_label} · by {t.created_by_name}</div>
              </div>
            </div>
          ))}
        </div>}
    </div>
  );
}

export function DetailsTab({ members, pins, onUnpin, canPin }) {
  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-4" data-testid="chat-details-tab">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Pinned ({pins?.length || 0})</div>
        {(pins || []).length === 0 ? <div className="text-xs text-zinc-500">Nothing pinned. Double-tap a message and hit the pin.</div> :
          <div className="space-y-2">
            {pins.map((p) => (
              <div key={p.id} className="bg-[#1E1712] border border-orange-900/40 rounded-xl p-3" data-testid={`pin-row-${p.id}`}>
                <div className="flex items-start gap-2">
                  <Pin className="w-3.5 h-3.5 text-[#FF5F15] mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0 text-xs text-zinc-200">
                    {p.text || (p.audio_path ? "🎤 Voice note" : "📷 Photo")}
                    <div className="text-[10px] text-zinc-500 mt-1">Pinned by {p.pinned?.by_name}</div>
                  </div>
                  {canPin && (
                    <button onClick={() => onUnpin(p.id)} className="text-zinc-400 hover:text-red-600" data-testid={`unpin-${p.id}`} aria-label="Unpin"><X className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>}
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Members ({members?.total || 0})</div>
        <div className="space-y-1.5">
          {(members?.members || []).map((m) => (
            <div key={m.id} className="flex items-center gap-2.5 py-1">
              <MemberAvatar m={m} size={30} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-zinc-100 truncate">{m.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{m.role}{m.online ? " · online" : ""}</div>
              </div>
              {m.phone && <a href={`tel:${m.phone}`} className="text-zinc-400 hover:text-emerald-600" aria-label={`Call ${m.name}`}><Phone className="w-4 h-4" /></a>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function LocationPin({ location, mine }) {
  if (!location) return null;
  return (
    <a href={`https://maps.google.com/?q=${encodeURIComponent(location)}`} target="_blank" rel="noreferrer"
       className={`inline-flex items-center gap-1 text-[11px] mt-1 ${mine ? "text-white/85" : "text-[#FF5F15]"}`}
       data-testid="chat-location-pin">
      <MapPin className="w-3 h-3" /> Location
    </a>
  );
}

export function SiteNoteChip({ siteNote, messageId }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [removed, setRemoved] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!siteNote || removed) return null;

  const openEditor = async () => {
    try {
      const { data } = await api.get(`/chat/messages/${messageId}/site-note`);
      setText(data.text || "");
      setOpen(true);
    } catch {
      toast.error("Couldn't load the note");
    }
  };
  const save = async () => {
    if (!text.trim()) { toast.error("Note can't be empty"); return; }
    setBusy(true);
    try {
      await api.patch(`/chat/messages/${messageId}/site-note`, { text: text.trim() });
      toast.success("Site note updated");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update the note");
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this site note from the job's diary?")) return;
    setBusy(true);
    try {
      await api.delete(`/chat/messages/${messageId}/site-note`);
      toast.success("Site note removed");
      setOpen(false);
      setRemoved(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove the note");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={openEditor}
              className="inline-flex items-center gap-1.5 mt-1 bg-[#2A1D0C] border border-[#FF5F15]/40 text-[#FFB27D] text-[10px] font-bold px-2.5 py-1 rounded-full hover:border-[#FF5F15] transition-colors"
              data-testid="chat-site-note-chip">
        <StickyNote className="w-3 h-3 text-[#FF5F15]" /> Site Note Created · {siteNote.job_label}
      </button>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setOpen(false)} data-testid="site-note-edit-dialog">
          <div className="bg-[#161618] w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-display font-black uppercase tracking-tight text-sm text-white">Edit site note</span>
              <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white" data-testid="site-note-edit-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">{siteNote.job_label}</div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              className="w-full bg-[#0B1018] border border-zinc-700 rounded-xl p-3 text-sm text-zinc-100 focus:outline-none focus:border-[#FF5F15]"
              data-testid="site-note-edit-input"
            />
            <div className="flex gap-2 mt-3">
              <button type="button" onClick={remove} disabled={busy}
                      className="px-3 py-2 rounded-xl border border-red-600/50 text-red-400 text-xs font-bold uppercase tracking-wider hover:bg-red-600/10 disabled:opacity-40"
                      data-testid="site-note-edit-remove">
                Remove note
              </button>
              <button type="button" onClick={save} disabled={busy}
                      className="flex-1 px-3 py-2 rounded-xl bg-[#FF5F15] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#E65100] disabled:opacity-40"
                      data-testid="site-note-edit-save">
                {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function usePresence() {
  useEffect(() => {
    const beat = () => api.post("/chat/presence").catch(() => {});
    beat();
    const t = setInterval(beat, 60000);
    return () => clearInterval(t);
  }, []);
}
